import { useMemo } from 'react';
import { useApiQuery } from '../../../../common/infrastructure/hooks/useApiQuery';
import { endpointsMetadata } from '../../../../common/infrastructure/routes/endpointsMetadata';
import { IEmployeeAvailability } from '../models/IEmployee';

interface IAppointmentEmployeeResponse {
  id: number;
  firstName: string;
  lastName: string;
  fullName: string;
  isAvailable: boolean;
  hasPortfolio: boolean;
}

/**
 * Hook to fetch available employees for a specific service
 */
export const useAppointmentEmployees = (serviceId: number | undefined, studioId: number | undefined) => {
  const { data: employeesData, isLoading, error } = useApiQuery<IAppointmentEmployeeResponse[]>({
    queryKey: [endpointsMetadata.appointments.getAvailableEmployees, serviceId, studioId],
    endpoint: `${endpointsMetadata.appointments.getAvailableEmployees}?serviceId=${serviceId}&studioId=${studioId}`,
    enabled: !!serviceId && !!studioId,
    globalIsLoading: false,
  });

  // Convert backend data to frontend format
  const employees = useMemo((): IEmployeeAvailability[] => {
    if (!employeesData) return [];
    
    const realEmployees = employeesData
      .filter(employee => employee.isAvailable)
      .map(employee => ({
        id: employee.id,
        name: employee.fullName,
        role: 'Специалист', // Default role since backend doesn't provide this
        avatar: employee.firstName.charAt(0).toUpperCase(), // Use first letter as avatar
        priceModifier: 1.0, // Default price modifier
        isAvailable: employee.isAvailable,
        hasPortfolio: employee.hasPortfolio,
        bookedSlots: [], // Will be populated when we fetch time slots
        workingHours: {} // Will be populated when we fetch working hours
      }));

    if (realEmployees.length === 0) return [];

    // Add "Any professional" option at the beginning
    const anyProfessional: IEmployeeAvailability = {
      id: -1, // Special ID for "Any professional"
      name: "Без значение",
      role: "за максимална наличност",
      avatar: "👥",
      priceModifier: 1.0,
      isAvailable: true,
      bookedSlots: [],
      workingHours: {}
    };

    return [anyProfessional, ...realEmployees];
  }, [employeesData]);

  return {
    employees,
    isLoading,
    error,
    hasData: !!employeesData && employeesData.length > 0
  };
};